/**
 * What the edit check panel says about checks written from the study's
 * uploaded data dictionary (D7, 2026-09-16). Pure, so it is tested from
 * functions/ (vitest.web.config.ts).
 *
 * The shapes mirror functions/src/models/editCheckTypes.ts
 * (EditCheckDictionarySummary, EditCheckNotWritten, EditCheckDictionarySource).
 */

import { familyLabel } from './notWrittenDisplay';

export type DictionaryColumn = 'required' | 'validation_rule' | 'visibility_rule';

export interface DictionarySource {
  column: DictionaryColumn;
  value: string;
  visibilityRule?: string;
}

export interface NotWrittenValue {
  form: string;
  field: string;
  column: DictionaryColumn;
  value: string;
  reason: string;
}

export interface DictionaryChecksSummary {
  written: { required: number; range: number; hidden: number };
  replaced: Array<{ id: string; name: string; crfForm: string; fieldName: string; checkType: string; rule: string }>;
  replacedCount: number;
  notWritten: NotWrittenValue[];
  notWrittenCount: number;
}

export type SourceFilter = '' | 'dictionary' | 'family' | 'default_range' | 'generated';

const COLUMN_WORDS: Record<DictionaryColumn, string> = {
  required: 'Required',
  validation_rule: 'Range',
  visibility_rule: 'Show/hide rule',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function dictionarySummaryText(s: DictionaryChecksSummary): {
  headline: string;
  replaced?: string;
  notWritten?: string;
  listCut?: string;
} {
  const { required, range, hidden } = s.written;
  const total = required + range + hidden;
  const parts = [
    required ? plural(required, 'required answer', 'required answers') : '',
    range ? plural(range, 'range', 'ranges') : '',
    hidden ? plural(hidden, 'show/hide rule', 'show/hide rules') : '',
  ].filter(Boolean);
  const headline = total === 0
    ? 'No checks come from your data dictionary.'
    : `${plural(total, 'check comes', 'checks come')} from your data dictionary: ${parts.join(', ')}.`;

  const out: ReturnType<typeof dictionarySummaryText> = { headline };
  if (s.replacedCount > 0) {
    out.replaced = `They replace ${plural(s.replacedCount, 'check', 'checks')} the generator had written for the same questions.`;
  }
  if (s.notWrittenCount > 0) {
    out.notWritten = s.notWrittenCount === 1
      ? '1 value in your dictionary did not become a check. It is listed below with the reason.'
      : `${s.notWrittenCount} values in your dictionary did not become a check. Each is listed below with the reason.`;
  }
  if (s.notWritten.length < s.notWrittenCount) {
    out.listCut = `Showing the first ${s.notWritten.length} of ${s.notWrittenCount}.`;
  }
  return out;
}

export function notWrittenLine(n: NotWrittenValue): string {
  return `${n.form} · ${n.field} · ${COLUMN_WORDS[n.column] ?? n.column} "${n.value}": ${n.reason}`;
}

const STANDARD_RULE_WORDS: Record<string, string> = {
  date_order: 'standard date-order check',
  future_date: 'standard future-date check',
  matching_limit: 'limit matched from an existing check',
};

type MaybeSourced = {
  source?: string;
  standardRule?: string;
  family?: string;
  dictionarySource?: DictionarySource;
  rangeSource?: string;
  defaultRange?: { min: number; max: number; unit: string };
};

/**
 * True for a standard plausibility range written because no protocol, settings,
 * CRF or dictionary range exists (Jim, 2026-10-12). It is never the protocol's.
 */
export function isDefaultRange(check: MaybeSourced): boolean {
  return check?.source === 'default_range' || check?.rangeSource === 'default';
}

/** What the default-range summary on the panel stores (EditCheckDefaultRangesSummary). */
export interface DefaultRangesSummary {
  written: number;
  notApplied: Array<{ form: string; field: string; reason: string }>;
  notAppliedCount: number;
}

/** One sentence for the panel; undefined when there are no default ranges or the spec predates them. */
export function defaultRangesText(s: DefaultRangesSummary | undefined | null): string | undefined {
  if (!s || !(s.written > 0)) return undefined;
  const used = `${plural(s.written, 'range check uses', 'range checks use')} a default plausibility range (sponsor to confirm). No range was found for ${s.written === 1 ? 'it' : 'them'} in the protocol checks, Study Settings or data dictionary.`;
  if (!(s.notAppliedCount > 0)) return used;
  return `${used} ${plural(s.notAppliedCount, 'numeric field has', 'numeric fields have')} neither a range nor a default.`;
}

export function isFromDictionary(check: MaybeSourced): boolean {
  return check?.source === 'dictionary';
}

export function checkSourceText(check: MaybeSourced): string | undefined {
  if (isDefaultRange(check)) {
    const r = check.defaultRange;
    return r
      ? `Default plausibility range (sponsor to confirm): ${r.min}-${r.max} ${r.unit}. Not from the protocol.`
      : 'Default plausibility range (sponsor to confirm). Not from the protocol.';
  }
  if (check?.source === 'family') {
    return check.family ? `Written by rule: ${familyLabel(check.family)}` : 'Written by rule';
  }
  // Standard data checks (standardChecks.ts, 2026-10-26).
  if (check?.source === 'standard') {
    return `Written by rule: ${STANDARD_RULE_WORDS[check.standardRule ?? ''] ?? 'standard data check'}`;
  }
  if (!isFromDictionary(check) || !check.dictionarySource) return undefined;
  const { column, value, visibilityRule } = check.dictionarySource;
  const shown = visibilityRule ? `, shown when "${visibilityRule}"` : '';
  return `Your data dictionary: ${COLUMN_WORDS[column] ?? column} "${value}"${shown}`;
}

export function matchesSourceFilter(check: MaybeSourced, filter: SourceFilter | string): boolean {
  if (filter === 'dictionary') return isFromDictionary(check);
  // 'family': written by rule from the study facts (docgen ECS, 2026-09-30).
  // Standard data checks are written by rule too (2026-10-26).
  if (filter === 'family') return check?.source === 'family' || check?.source === 'standard';
  if (filter === 'default_range') return isDefaultRange(check);
  if (filter === 'generated') return !isFromDictionary(check) && check?.source !== 'family' && check?.source !== 'standard' && !isDefaultRange(check);
  return true;
}
